/**
 * SPDX-FileCopyrightText: 2025 Nextcloud GmbH and Nextcloud contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

import type {
	ComputedRef,
	InjectionKey,
	Ref,
} from 'vue'
import type { RouteLocation } from 'vue-router'
import type {
	ChatMessage,
	Conversation,
} from '../types/index.ts'

import { isCancel } from '@nextcloud/axios'
import { subscribe, unsubscribe } from '@nextcloud/event-bus'
import { t } from '@nextcloud/l10n'
import { computed, inject, onBeforeUnmount, provide, ref, watch } from 'vue'
import { START_LOCATION, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { ATTENDEE, CHAT, MESSAGE } from '../constants.ts'
import { EventBus } from '../services/EventBus.ts'
import { useChatStore } from '../stores/chat.ts'
import { useChatExtrasStore } from '../stores/chatExtras.ts'
import { useGuestNameStore } from '../stores/guestName.ts'
import { isAxiosErrorResponse } from '../types/guards.ts'
import { debugTimer } from '../utils/debugTimer.ts'
import { isFileShareMessage, tryLocalizeDeletedMessage, tryLocalizeSystemMessage } from '../utils/message.ts'
import { useGetThreadId } from './useGetThreadId.ts'
import { useGetToken } from './useGetToken.ts'

type GetMessagesContext = {
	contextMessageId: Ref<number>
	loadingOldMessages: Ref<boolean>
	loadingNewMessages: Ref<boolean>
	isInitialisingMessages: Ref<boolean>
	isChatBeginningReached: ComputedRef<boolean>
	isChatEndReached: ComputedRef<boolean>

	getOldMessages: (token: string, includeLastKnown: boolean) => Promise<void>
	getNewMessages: (token: string, includeLastKnown: boolean) => Promise<void>
}

const GET_MESSAGES_CONTEXT_KEY: InjectionKey<GetMessagesContext> = Symbol.for('GET_MESSAGES_CONTEXT')

let pollingTimeout: ReturnType<typeof setTimeout> | undefined
let expirationInterval: ReturnType<typeof setInterval> | undefined
let pollingErrorTimeout = 1_000
let chatRelaySupported: boolean | null = null
let fallbackPollInterval: ReturnType<typeof setInterval> | undefined

/**
 * Composable to provide control logic for fetching messages list
 */
export function useGetMessagesProvider() {
	const store = useStore()
	const route = useRoute()
	const chatStore = useChatStore()
	const chatExtrasStore = useChatExtrasStore()
	const guestNameStore = useGuestNameStore()

	const currentToken = useGetToken()
	const contextThreadId = useGetThreadId()
	/** Conversation object by currentToken ref. If required in an async context, store getter should be used instead */
	const currentConversation = computed<Conversation | undefined>(() => store.getters.conversation(currentToken.value))
	const isInLobby = computed<boolean>(() => store.getters.isInLobby)

	const contextMessageId = ref<number>(0)
	const loadingOldMessages = ref(false)
	const loadingNewMessages = ref(false)
	const isInitialisingMessages = ref(true)
	const stopFetchingOldMessages = ref(false)

	/**
	 * Returns whether the current participant is a participant of current conversation.
	 */
	const isParticipant = computed<boolean>(() => {
		if (!currentConversation.value) {
			return false
		}

		return !!store.getters.findParticipant(currentToken.value, currentConversation.value)?.attendeeId
	})

	const isChatBeginningReached = computed(() => {
		if (stopFetchingOldMessages.value) {
			return true
		}
		const firstKnownMessageId = chatStore.getFirstKnownId(currentToken.value, { messageId: contextMessageId.value, threadId: contextThreadId.value })
		const firstKnownMessage = store.getters.message(currentToken.value, firstKnownMessageId) as ChatMessage | undefined
		if (!firstKnownMessage) {
			// Do not block attempts to fetch history inside each block
			return false
		}

		if (contextThreadId.value) {
			// If threadId is set, we should check if the first message is from the thread
			return firstKnownMessage.id === contextThreadId.value
		}

		return firstKnownMessage.messageType === MESSAGE.TYPE.SYSTEM
			&& [MESSAGE.SYSTEM_TYPE.CONVERSATION_CREATED, MESSAGE.SYSTEM_TYPE.HISTORY_CLEARED].includes(firstKnownMessage.systemMessage)
	})

	const conversationLastMessageId = computed<number>(() => {
		if (contextThreadId.value) {
			const threadInfo = chatExtrasStore.threads[currentToken.value]?.[contextThreadId.value]
			// If threadId is set, we should compare with last message from the thread
			if (threadInfo) {
				return threadInfo.last?.id ?? contextThreadId.value
			}
		}

		if (currentConversation.value?.lastMessage && 'id' in currentConversation.value.lastMessage) {
			return currentConversation.value.lastMessage.id
		}

		// Federated conversations do not provide lastMessage.id, fallback to last known message
		return chatStore.getLastKnownId(currentToken.value, { threadId: contextThreadId.value })
	})

	const isChatEndReached = computed(() => {
		if (!currentConversation.value || !currentConversation.value.lastMessage) {
			// Do not block attempts to fetch new messages inside each block
			return false
		}

		const lastKnownMessageId = chatStore.getLastKnownId(currentToken.value, { messageId: contextMessageId.value, threadId: contextThreadId.value })

		return lastKnownMessageId >= conversationLastMessageId.value
	})

	watch(
		[currentToken, () => isParticipant.value && !isInLobby.value],
		([newToken, canGetMessages], [oldToken, _oldCanGetMessages]) => {
			if (route.name === START_LOCATION.name) { // Direct object comparison does not work
				// Skip potential initial check to ensure route is available
				return
			}
			if (oldToken && oldToken !== newToken) {
				store.dispatch('cancelPollNewMessages', { requestId: oldToken })
				chatRelaySupported = null
				clearInterval(fallbackPollInterval)
			}

			if (newToken && canGetMessages) {
				handleStartGettingMessagesPreconditions(newToken)
			} else {
				store.dispatch('cancelPollNewMessages', { requestId: newToken })
			}

			/** Remove expired messages when joining a room */
			store.dispatch('removeExpiredMessages', { token: newToken })
		},
		{ immediate: true },
	)

	subscribe('networkOffline', handleNetworkOffline)
	subscribe('networkOnline', handleNetworkOnline)
	EventBus.on('route-change', onRouteChange)
	EventBus.on('set-context-id-to-bottom', setContextIdToBottom)
	EventBus.on('signaling-message-received', addMessageFromChatRelay)
	EventBus.on('signaling-supported-features', checkChatRelaySupport)
	EventBus.on('should-refresh-chat-messages', tryPollNewMessages)

	/** Every 30 seconds we remove expired messages from the store */
	expirationInterval = setInterval(() => {
		store.dispatch('removeExpiredMessages', { token: currentToken.value })
	}, 30_000)

	onBeforeUnmount(() => {
		unsubscribe('networkOffline', handleNetworkOffline)
		unsubscribe('networkOnline', handleNetworkOnline)
		EventBus.off('route-change', onRouteChange)
		EventBus.off('set-context-id-to-bottom', setContextIdToBottom)
		EventBus.off('signaling-message-received', addMessageFromChatRelay)
		EventBus.off('signaling-supported-features', checkChatRelaySupport)
		EventBus.off('should-refresh-chat-messages', tryPollNewMessages)

		store.dispatch('cancelPollNewMessages', { requestId: currentToken.value })
		clearInterval(pollingTimeout)
		clearInterval(expirationInterval)
	})

	/**
	 * Parse hash string to get message id
	 *
	 * @param hash
	 */
	function getMessageIdFromHash(hash?: string): number | null {
		return (hash && hash.startsWith('#message_')) ? parseInt(hash.slice(9), 10) : null
	}

	/**
	 * Stop polling due to offline
	 */
	function handleNetworkOffline() {
		if (currentToken.value) {
			console.debug('Canceling message request as we are offline')
			store.dispatch('cancelPollNewMessages', { requestId: currentToken.value })
		}
	}

	/**
	 * Resume polling, when back online
	 */
	function handleNetworkOnline() {
		if (currentToken.value) {
			console.debug('Restarting polling of new chat messages')
			pollNewMessages(currentToken.value)
		}
	}

	/**
	 * Handle route changes to initialize chat or thread, and focus given message
	 *
	 * @param payload
	 * @param payload.from
	 * @param payload.to
	 */
	async function onRouteChange({ from, to }: { from: RouteLocation, to: RouteLocation }) {
		// Reset blocker for fetching old messages
		stopFetchingOldMessages.value = false
		// Reset messages list to default view
		chatExtrasStore.setShowScheduledMessages(false)

		if (from.name !== 'conversation' || to.name !== 'conversation'
			|| from.params.token !== to.params.token || typeof to.params.token !== 'string') {
			// Only handle route changes within the same conversation
			return
		}

		const focusMessageId = getMessageIdFromHash(to.hash)
		if (focusMessageId !== null) {
			// the hash is non-empty, need to focus/highlight another message
			contextMessageId.value = focusMessageId
		} else {
			const conversation: Conversation | undefined = store.getters.conversation(to.params.token)
			// try to focus last read message first, otherwise scroll to last known message in the most recent block store
			const hasLastReadMessageInContextBelow = conversation?.lastReadMessage && conversation.lastReadMessage > contextMessageId.value
				&& (!contextThreadId.value || chatStore.hasMessage(to.params.token, { messageId: conversation.lastReadMessage, threadId: contextThreadId.value }))

			contextMessageId.value = hasLastReadMessageInContextBelow
				? conversation.lastReadMessage
				: conversationLastMessageId.value
		}

		await checkContextAndFocusMessage(to.params.token, contextMessageId.value, contextThreadId.value, focusMessageId !== null)
	}

	/**
	 * Update contextMessageId to the last message in the conversation
	 *
	 * @param token
	 * @param messageId
	 * @param threadId
	 * @param highlight
	 * @param focus
	 */
	async function checkContextAndFocusMessage(token: string, messageId: number, threadId: number, highlight: boolean = false, focus: boolean = true) {
		if (!chatStore.hasMessage(token, { messageId, threadId })) {
			// message not found in the list, need to fetch it first
			await getMessageContext(token, messageId, threadId)
		} else {
			const firstContextMessageId = chatStore.getFirstKnownId(token, { messageId, threadId })
			const nearestContextMessageId = chatStore.getNearestKnownContextId(token, { messageId, threadId })

			if (!nearestContextMessageId) {
				// current context is empty, need to fetch it first
				await getMessageContext(token, messageId, threadId)
			} else if (nearestContextMessageId !== messageId) {
				// message to be shown does not belong to the current context, switch to nearest instead
				contextMessageId.value = nearestContextMessageId
				messageId = nearestContextMessageId
			}

			if (messageId === firstContextMessageId || !chatStore.hasEnoughMessages(token, { messageId, threadId })) {
				// message is the first one in the block, try to get some messages above
				isInitialisingMessages.value = true
				await getOldMessages(token, false, { messageId, threadId })
				isInitialisingMessages.value = false
			}
		}

		if (focus) {
			// need some delay (next tick is too short) to be able to run
			// after the browser's native "scroll to anchor" from the hash
			window.setTimeout(() => {
				EventBus.emit('focus-message', { messageId, highlight })
			}, 2)
		}
	}

	/**
	 * Update contextMessageId to the last message in the conversation
	 */
	async function setContextIdToBottom() {
		contextMessageId.value = conversationLastMessageId.value
		await checkContextAndFocusMessage(currentToken.value, contextMessageId.value, contextThreadId.value, false, false)
	}

	/**
	 * Initialize chat context borders and start fetching messages
	 *
	 * @param token token of conversation where a method was called
	 */
	async function handleStartGettingMessagesPreconditions(token: string) {
		// prevent sticky mode before we have loaded anything
		isInitialisingMessages.value = true

		// Start from message hash or unread marker
		const focusMessageId = getMessageIdFromHash(route.hash)
		contextMessageId.value = focusMessageId !== null ? focusMessageId : currentConversation.value!.lastReadMessage

		store.dispatch('setVisualLastReadMessageId', { token, id: currentConversation.value!.lastReadMessage })

		if (!chatStore.chatBlocks[token]) {
			try {
				// TODO id previously could be 0 in this place, need to block fetching the chat from beginning
				if (!contextMessageId.value) {
					throw new Error(`[DEBUG] spreed: context message ID is ${contextMessageId.value}`)
				}

				await getMessageContext(token, contextMessageId.value, contextThreadId.value)
			} catch (exception) {
				console.debug(exception)
			}

			// Checking after server response, compare to actual conversation in store
			const conversation: Conversation | undefined = store.getters.conversation(token)
			// If last message is not present in the initial context,
			// add it as most recent chat block to start long polling from it
			if (conversation?.lastMessage && 'id' in conversation.lastMessage
				&& !chatStore.hasMessage(token, { messageId: conversation.lastMessage.id })) {
				await store.dispatch('processMessage', { token, message: conversation.lastMessage })
				chatStore.processChatBlocks(token, [conversation.lastMessage])
			}

			// Fallback for sensitive and federated conversations: if there is still no chat block created,
			// ensure polling starts at least from the last read message by the user
			if (!chatStore.chatBlocks[token]) {
				chatStore.chatBlocks[token] = [new Set([conversation!.lastReadMessage])]
			}
		} else {
			await checkContextAndFocusMessage(token, contextMessageId.value, contextThreadId.value, focusMessageId !== null)
		}

		isInitialisingMessages.value = false

		if (chatRelaySupported !== null) {
			// Case: chat relay is confirmed to be supported / not supported from signaling hello message,
			// but polling was not immediately triggered (e.g, when received while context request is ongoing)
			pollNewMessages(token)
		} else {
			// Fallback polling in case signaling does not work and we will never receive Hello message
			// chatRelaySupported is still null (signaling hello was not received yet)
			pollingTimeout = setTimeout(() => {
				if (chatRelaySupported) {
					return
				}
				pollNewMessages(token)
			}, 30_000)
		}
	}

	/**
	 * Fetches the messages of a conversation given the conversation token.
	 * Creates a long polling request for new messages.
	 *
	 * @param token token of conversation where a method was called
	 * @param messageId context messageId
	 * @param threadId context thread id
	 */
	async function getMessageContext(token: string, messageId: number, threadId: number) {
		isInitialisingMessages.value = true
		loadingOldMessages.value = true
		try {
			debugTimer.start(`${token} | get context`)
			// Make the request
			await store.dispatch('getMessageContext', {
				token,
				// If MESSAGE.CHAT_BEGIN_ID we need to get the context from the beginning
				// using 0 as the API does not support negative values
				// Get chat messages before last read message and after it
				messageId: messageId !== MESSAGE.CHAT_BEGIN_ID ? messageId : 0,
				threadId: threadId !== 0 ? threadId : undefined,
				minimumVisible: CHAT.MINIMUM_VISIBLE,
			})
			debugTimer.end(`${token} | get context`, 'status 200')
		} catch (exception) {
			if (isCancel(exception)) {
				console.debug('The request has been canceled', exception)
				debugTimer.end(`${token} | get context`, 'cancelled')
				loadingOldMessages.value = false
				throw exception
			}

			if (isAxiosErrorResponse(exception) && exception.response?.status === 304) {
				// 304 - Not modified
				// Empty chat, no messages to load
				debugTimer.end(`${token} | get context`, 'status 304')
				store.dispatch('loadedMessagesOfConversation', { token })
				stopFetchingOldMessages.value = true
			}
		}
		loadingOldMessages.value = false
		isInitialisingMessages.value = false
	}

	/**
	 * Get messages history.
	 *
	 * @param token token of conversation where a method was called
	 * @param includeLastKnown Include or exclude the last known message in the response
	 * @param payload Optional payload to pass additional parameters (messageId, threadId)
	 * @param payload.messageId
	 * @param payload.threadId
	 */
	async function getOldMessages(token: string, includeLastKnown: boolean, payload?: { messageId?: number, threadId?: number }) {
		if (isChatBeginningReached.value) {
			// Beginning of the chat reached, no more messages to load
			return
		}
		// Make the request
		loadingOldMessages.value = true
		const lastKnownMessageId = payload?.messageId ?? chatStore.getFirstKnownId(token, { messageId: contextMessageId.value, threadId: contextThreadId.value })
		const threadId = payload?.threadId ?? contextThreadId.value !== 0 ? contextThreadId.value : undefined
		try {
			debugTimer.start(`${token} | fetch history`)
			await store.dispatch('fetchMessages', {
				token,
				lastKnownMessageId,
				includeLastKnown,
				lookIntoFuture: CHAT.FETCH_OLD,
				threadId,
				minimumVisible: CHAT.MINIMUM_VISIBLE,
			})
			debugTimer.end(`${token} | fetch history`, 'status 200')
		} catch (exception) {
			if (isCancel(exception)) {
				debugTimer.end(`${token} | fetch history`, 'cancelled')
				console.debug('The request has been canceled', exception)
			}
			if (isAxiosErrorResponse(exception) && exception?.response?.status === 304) {
				// 304 - Not modified
				debugTimer.end(`${token} | fetch history`, 'status 304')
				stopFetchingOldMessages.value = true
			}
		}
		loadingOldMessages.value = false
	}

	/**
	 * Get messages history (for new messages).
	 *
	 * @param token token of conversation where a method was called
	 * @param includeLastKnown Include or exclude the last known message in the response
	 * @param payload Optional payload to pass additional parameters (messageId, threadId)
	 * @param payload.messageId
	 * @param payload.threadId
	 */
	async function getNewMessages(token: string, includeLastKnown: boolean, payload?: { messageId?: number, threadId?: number }) {
		if (isChatEndReached.value) {
			// End of the chat reached, do not conflict with polling
			return
		}

		const lastKnownMessageId = payload?.messageId ?? chatStore.getLastKnownId(token, { messageId: contextMessageId.value, threadId: contextThreadId.value })
		const pollingLastKnownMessageId = chatStore.getLastKnownId(token)
		if (!chatRelaySupported && lastKnownMessageId === pollingLastKnownMessageId) {
			// Do not make parallel request with polling
			return
		}

		// Make the request
		loadingNewMessages.value = true
		const threadId = payload?.threadId ?? contextThreadId.value !== 0 ? contextThreadId.value : undefined
		try {
			debugTimer.start(`${token} | fetch history (new)`)
			await store.dispatch('fetchMessages', {
				token,
				lastKnownMessageId,
				threadId,
				includeLastKnown,
				lookIntoFuture: CHAT.FETCH_NEW,
				minimumVisible: CHAT.MINIMUM_VISIBLE,
			})
			debugTimer.end(`${token} | fetch history (new)`, 'status 200')
		} catch (exception) {
			if (isCancel(exception)) {
				debugTimer.end(`${token} | fetch history (new)`, 'cancelled')
				console.debug('The request has been canceled', exception)
			}
			if (isAxiosErrorResponse(exception) && exception?.response?.status === 304) {
				// 304 - Not modified
				debugTimer.end(`${token} | fetch history (new)`, 'status 304')
			}
		}
		loadingNewMessages.value = false
	}

	/**
	 * Fetches the messages of a conversation given the conversation token.
	 * Creates a long polling request for new messages.
	 *
	 * @param token token of conversation where a method was called
	 */
	async function pollNewMessages(token: string) {
		// Check that the token has not changed
		if (currentToken.value !== token) {
			console.debug(`token has changed to ${currentToken.value}, breaking the loop for ${token}`)
			return
		}

		// If chat was previously opened, and messages were received via chat-relay,
		// start polling from last received by server message id
		const lastKnownMessageId = chatStore.getLastServerResponseId(token) ?? chatStore.getLastKnownId(token)

		// Make the request
		try {
			debugTimer.start(`${token} | long polling`)
			// TODO: move polling logic to the store and also cancel timers on cancel
			const response = await store.dispatch('pollNewMessages', {
				token,
				lastKnownMessageId,
				requestId: token,
				timeout: chatRelaySupported ? 0 : undefined,
			})

			chatStore.setLastServerResponseId(token, response.data.ocs.data
				.reduce((acc: number, message: ChatMessage) => {
					return message.id > acc ? message.id : acc
				}, lastKnownMessageId))

			pollingErrorTimeout = 1_000
			debugTimer.end(`${token} | long polling`, 'status 200')
		} catch (exception) {
			if (isCancel(exception)) {
				debugTimer.end(`${token} | long polling`, 'cancelled')
				console.debug('The request has been canceled', exception)
				return
			}

			if (isAxiosErrorResponse(exception) && exception?.response?.status === 304) {
				debugTimer.end(`${token} | long polling`, 'status 304')
				// 304 - Not modified
				chatStore.setLastServerResponseId(token, lastKnownMessageId)
				// This is not an error, so reset error timeout and poll again
				pollingErrorTimeout = 1_000
				clearTimeout(pollingTimeout)
				if (chatRelaySupported) {
					restartFallbackPollNewMessages()
					return
				}
				pollingTimeout = setTimeout(() => {
					pollNewMessages(token)
				}, 500)
				return
			}

			if (pollingErrorTimeout < 30_000) {
				// Delay longer after each error
				pollingErrorTimeout += 5_000
			}

			debugTimer.end(`${token} | long polling`, `status ${isAxiosErrorResponse(exception) ? exception?.response?.status : 'unknown'}`)
			console.debug('Error happened while getting chat messages. Trying again in %d seconds', pollingErrorTimeout / 1_000, exception)

			clearTimeout(pollingTimeout)
			if (chatRelaySupported) {
				restartFallbackPollNewMessages()
				return
			}
			pollingTimeout = setTimeout(() => {
				pollNewMessages(token)
			}, pollingErrorTimeout)
			return
		}

		clearTimeout(pollingTimeout)
		if (chatRelaySupported) {
			restartFallbackPollNewMessages()
			return
		}
		pollingTimeout = setTimeout(() => {
			pollNewMessages(token)
		}, 500)
	}

	/**
	 *
	 */
	function tryPollNewMessages() {
		if (!chatRelaySupported) {
			// the event is only relevant when chat relay is supported
			return
		}
		pollNewMessages(currentToken.value)
	}

	/**
	 * Check whether chat relay is supported
	 *
	 * @param features
	 */
	async function checkChatRelaySupport(features: string[]) {
		if (features.includes('chat-relay')) {
			chatRelaySupported = true
		} else {
			chatRelaySupported = false
		}

		if (!pollingTimeout) {
			// Context request is still ongoing
			return
		}
		// Once the history and Hello signaling message is received, starts looking for new messages.
		clearTimeout(pollingTimeout)
		await pollNewMessages(currentToken.value)
		restartFallbackPollNewMessages()
	}

	/**
	 * Start over the interval for fallback polling (chatRelaySupported only)
	 * Every 2 minutes perform fetch from last message given by server
	 */
	function restartFallbackPollNewMessages() {
		clearInterval(fallbackPollInterval)
		fallbackPollInterval = setInterval(fallbackPollNewMessages, 120_000)
	}

	/**
	 * Get messages history (fallback for chat-relay).
	 */
	async function fallbackPollNewMessages() {
		const token = currentToken.value
		const lastKnownMessageId = chatStore.getLastServerResponseId(token)
		if (!lastKnownMessageId || !token) {
			return
		}

		// Make the request
		try {
			const response = await store.dispatch('fetchMessages', {
				token,
				lastKnownMessageId,
				includeLastKnown: false,
				lookIntoFuture: CHAT.FETCH_NEW,
				minimumVisible: 0, // handle as many as server gives
			})

			chatStore.setLastServerResponseId(token, response.data.ocs.data.reduce((acc: number, message: ChatMessage) => {
				return message.id > acc ? message.id : acc
			}, lastKnownMessageId))
		} catch (exception) {
			if (isCancel(exception)) {
				console.debug('The request has been canceled', exception)
			}
			if (isAxiosErrorResponse(exception) && exception?.response?.status === 304) {
				// 304 - Not modified
			}
		}
	}

	/**
	 * Chat relay sends one message at a time, we update our stores directly
	 *
	 * @param payload - signaling event payload
	 * @param payload.token - conversation token, where event was sent
	 * @param payload.message - chat message from the event
	 * @param [payload.lastCommonReadMessage] - optional last common read marker
	 */
	function addMessageFromChatRelay(payload: { token: string, message: ChatMessage, lastCommonReadMessage?: number }) {
		if (!chatRelaySupported) {
			// chat relay is not supported, ignore the message
			return
		}

		const { token, message, lastCommonReadMessage } = payload
		if (token !== currentToken.value) {
			// Guard: Message is for another conversation
			// e.g., user switched conversation while messages were in-flight
			return
		}

		if ([ATTENDEE.ACTOR_TYPE.GUESTS, ATTENDEE.ACTOR_TYPE.EMAILS].includes(message.actorType)) {
			// update guest display names cache
			// force in case the display name has changed since
			// the last fetch
			guestNameStore.addGuestName(message, { noUpdate: false })
		}

		const conversation: Conversation | undefined = store.getters.conversation(token)
		// Attempt to localize non-system messages
		if (message.systemMessage !== '' && conversation) {
			try {
				message.message = tryLocalizeSystemMessage(message, conversation)
			} catch (exception) {
				tryPollNewMessages()
				return
			}
		}

		// Attempt to localize deleted messages
		if (message.systemMessage === MESSAGE.SYSTEM_TYPE.MESSAGE_DELETED && conversation
			&& 'parent' in message && message.parent && 'message' in message.parent) {
			try {
				message.parent.message = tryLocalizeDeletedMessage(message.parent, conversation)
			} catch (exception) {
				tryPollNewMessages()
				return
			}
		}

		// FIXME Patch for file uploads: messageParameters['file']['path'] and messageParameters['file']['link'] do not match server request
		// Additionally check messages and system messages with parent
		if (isFileShareMessage(message) || (message.parent && 'messageParameters' in message.parent && isFileShareMessage(message.parent))) {
			tryPollNewMessages()
			return
		}

		// Patch for federated conversations: disable unsupported file shares
		if (conversation?.remoteServer && isFileShareMessage(message)
			&& [MESSAGE.TYPE.COMMENT, MESSAGE.TYPE.VOICE_MESSAGE, MESSAGE.TYPE.RECORD_VIDEO, MESSAGE.TYPE.RECORD_AUDIO].includes(message.messageType)) {
			message.message = '*' + t('spreed', 'File shares are currently not supported in federated conversations') + '*'
			delete message.messageParameters.file
		}

		chatStore.processChatBlocks(token, [message], { mergeBy: chatStore.getLastKnownId(token) })
		store.dispatch('processMessage', { token, message, fromRealtime: true })
		if (lastCommonReadMessage !== undefined) {
			// do not compare with conversation.lastCommonReadMessage
			// value from signaling is actual, but not incremental (can be lesser or equal 0)
			store.dispatch('updateLastCommonReadMessage', { token, lastCommonReadMessage })
		}
	}

	provide(GET_MESSAGES_CONTEXT_KEY, {
		contextMessageId,
		loadingOldMessages,
		loadingNewMessages,
		isInitialisingMessages,
		isChatBeginningReached,
		isChatEndReached,

		getOldMessages,
		getNewMessages,
	})
}

/**
 * Composable to inject control logic for fetching messages list in the component
 */
export function useGetMessages() {
	return inject(GET_MESSAGES_CONTEXT_KEY)!
}
